/**
 * Network Adequacy TD Map — GIS-grade visualization of scan results, providers, facilities, and time/distance.
 * Layers: counties (choropleth), providers/facilities (clustered), access points (toggle), gap density.
 */
import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  CircleMarker,
  Popup,
  LayerGroup,
  useMap
} from 'react-leaflet';
import InfoTooltip from '../../components/InfoTooltip';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

function createCircleIcon(color, border = '#333', size = 18) {
  return L.divIcon({
    className: 'na-map-marker',
    html: `<span style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid ${border};display:inline-block;box-shadow:0 1px 4px rgba(0,0,0,0.4);pointer-events:auto"></span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}

const PROVIDER_ICON = createCircleIcon('#6366f1', '#4338ca');
const FACILITY_ICON = createCircleIcon('#0ea5e9', '#0284c7');

// County GeoJSON is proxied through the backend to avoid cross-origin fetch failures on Azure.
const COUNTY_GEOJSON_PATH = '/county-geojson';

/** Map provider: Mapbox when token set (REACT_APP_NA_MAPBOX_TILE_ACCESS_TOKEN or REACT_APP_MAPBOX_ACCESS_TOKEN), else env override or OpenStreetMap standard tiles (CARTO raster tiles began requiring a key and render a watermark). No hardcoded keys. */
const MAPBOX_TOKEN = process.env.REACT_APP_NA_MAPBOX_TILE_ACCESS_TOKEN || process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';
const MAP_STYLE = process.env.REACT_APP_NA_MAP_STYLE || 'streets-v12';
const MAP_TILE_URL = MAPBOX_TOKEN
  ? `https://api.mapbox.com/styles/v1/mapbox/${MAP_STYLE}/tiles/256/{z}/{x}/{y}@2x?access_token=${MAPBOX_TOKEN}`
  : (process.env.REACT_APP_NA_MAP_TILE_URL || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png');
const MAP_TILE_ATTRIBUTION = process.env.REACT_APP_NA_MAP_TILE_ATTRIBUTION ||
  (MAPBOX_TOKEN ? '&copy; <a href="https://www.mapbox.com/">Mapbox</a>' : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors');

const NA_MAP_DEBUG = process.env.REACT_APP_NA_MAP_DEBUG !== '0' &&
  (process.env.NODE_ENV === 'development' || process.env.REACT_APP_NA_MAP_DEBUG === '1' || process.env.REACT_APP_NA_MAP_DEBUG === 'true');
function naLog(...args) {
  if (NA_MAP_DEBUG) console.log('[NAMapView]', ...args);
}

const STATUS_COLORS = {
  compliant: '#22c55e',
  fragile: '#eab308',
  deficient: '#dc2626',
  none: 'rgba(148, 163, 184, 0.4)'
};

const DEFAULT_CENTER = [39.8283, -98.5795];
const DEFAULT_ZOOM = 4;

/** Fit map bounds to show providers/facilities when they load. */
function FitBoundsToMarkers({ providers = [], facilities = [] }) {
  const map = useMap();
  const points = useMemo(() => {
    const all = [
      ...(providers || []).map((p) => [p.latitude, p.longitude]),
      ...(facilities || []).map((f) => [f.latitude, f.longitude])
    ].filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));
    return all;
  }, [providers, facilities]);
  useEffect(() => {
    if (points.length === 0) return;
    const t0 = performance.now();
    try {
      map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 12 });
      naLog('FitBounds done', { points: points.length, elapsed: `${Math.round(performance.now() - t0)}ms` });
    } catch (_) { /* ignore */ }
  }, [map, points]);
  return null;
}

/** Clustered markers: uses Leaflet.markercluster — same approach as Google Maps, Mapbox, etc. All data, no cap. */
function ImperativeClusterLayer({ items = [], icon, label, onSelect, debugId }) {
  const map = useMap();
  const onSelectRef = React.useRef(onSelect);
  onSelectRef.current = onSelect;
  const runCountRef = React.useRef(0);
  useEffect(() => {
    if (!map || !items.length) return;
    runCountRef.current += 1;
    const t0 = performance.now();
    const cluster = L.markerClusterGroup({
      chunkedLoading: true,
      chunkInterval: 50,
      removeOutsideVisibleBounds: true,
      spiderfyOnMaxZoom: false,
      zoomToBoundsOnClick: false,
      maxClusterRadius: 80,
      disableClusteringAtZoom: 19
    });
    cluster.on('clusterclick', (e) => {
      map.fitBounds(e.layer.getBounds(), { padding: [40, 40], maxZoom: 19 });
    });
    const markers = [];
    for (const item of items) {
      const lat = Number(item.latitude);
      const lng = Number(item.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      const m = L.marker([lat, lng], { icon, riseOnHover: true });
      const name = (item.name || item.id || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      m.bindPopup(`<strong>${label}</strong> ${name}<br/>${(item.specialty_code || '')} · ${(item.ssa_state_county_code || '')}`);
      m.on('click', () => onSelectRef.current?.(item));
      markers.push(m);
    }
    map.addLayer(cluster);
    cluster.addLayers(markers);
    const elapsed = Math.round(performance.now() - t0);
    naLog(`ImperativeClusterLayer ${debugId || label}`, { run: runCountRef.current, count: markers.length, elapsed: `${elapsed}ms` });
    return () => map.removeLayer(cluster);
  }, [map, items, icon, label, debugId]);
  return null;
}

/**
 * Spread markers that share the same coordinates into a ring to reduce spiderfy clutter.
 * Groups by rounded (lat,lon); for groups with count>1, arranges in a circle around the center.
 */
function spreadDuplicateCoordinates(items, radiusDegPerSqrt = 0.0002, maxRadiusDeg = 0.012) {
  if (!items?.length) return items;
  const key = (lat, lon) => `${Math.round(lat * 1e5) / 1e5}|${Math.round(lon * 1e5) / 1e5}`;
  const groups = new Map();
  for (const item of items) {
    const lat = Number(item.latitude);
    const lon = Number(item.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    const k = key(lat, lon);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push({ ...item, latitude: lat, longitude: lon });
  }
  const result = [];
  for (const group of groups.values()) {
    if (group.length <= 1) {
      result.push(...group);
      continue;
    }
    const { latitude: lat, longitude: lon } = group[0];
    const radius = Math.min(radiusDegPerSqrt * Math.ceil(Math.sqrt(group.length)), maxRadiusDeg);
    const cosLat = Math.cos((lat * Math.PI) / 180);
    for (let i = 0; i < group.length; i++) {
      const angle = (2 * Math.PI * i) / group.length;
      const dLat = radius * Math.sin(angle);
      const dLon = (radius * Math.cos(angle)) / (cosLat || 1);
      result.push({ ...group[i], latitude: lat + dLat, longitude: lon + dLon });
    }
  }
  return result;
}

/** Normalize to 5-digit SSACD for consistent lookup. Pad 4-digit, take last 5 if 6-digit. */
function normalizeCountyCode(code) {
  if (!code) return '';
  const s = String(code).trim();
  const digits = s.replace(/\D/g, '');
  if (!digits) return '';
  return digits.length <= 5 ? digits.padStart(5, '0') : digits.slice(-5);
}

/** Reduce polygon vertex count to improve render performance. Keeps every Nth point. */
function simplifyRing(ring, keepEvery = 2) {
  if (!Array.isArray(ring) || ring.length < 4 || keepEvery <= 1) return ring;
  const out = [];
  for (let i = 0; i < ring.length; i++) {
    if (i % keepEvery === 0 || i === ring.length - 1) out.push(ring[i]);
  }
  if (out.length < 3) return ring;
  if (out[0][0] !== out[out.length - 1][0] || out[0][1] !== out[out.length - 1][1]) out.push(out[0]);
  return out;
}

/** Single source of truth: extract 5-digit SSACD/FIPS from GeoJSON feature. Handles Plotly (id, GEO_ID) and Census (GEOID, COUNTYFP+STATE). */
function extractCountyCodeFromFeature(feature) {
  if (!feature) return null;
  const id = feature.id;
  if (id != null && id !== '') {
    const s = String(id).trim();
    if (s.length >= 4 && s.length <= 6 && /^\d+$/.test(s)) return s.length === 4 ? '0' + s : s.length === 5 ? s : s.slice(-5);
  }
  const props = feature.properties || {};
  const geoid = props.GEOID ?? props.GEO_ID ?? props.geoid ?? props.geo_id;
  if (geoid != null && geoid !== '') {
    const m = String(geoid).match(/(\d{5})$/);
    if (m) return m[1];
  }
  const state = props.STATE ?? props.state ?? props.STATEFP ?? props.state_fips ?? '';
  const county = props.COUNTY ?? props.county ?? props.COUNTYFP ?? props.county_fips ?? '';
  if (state && county) {
    const combined = String(state).padStart(2, '0') + String(county).padStart(3, '0');
    if (combined.length === 5) return combined;
  }
  return null;
}

export default function NAMapView({ apiBase, scans = [], loadingScans, onRefreshScans }) {
  const [scanId, setScanId] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [mapDataLoading, setMapDataLoading] = useState(true);
  const [countyGeoJson, setCountyGeoJson] = useState(null);
  const [countyGeoJsonError, setCountyGeoJsonError] = useState(null);
  const [layers, setLayers] = useState({
    counties: false,
    providers: true,
    facilities: true,
    accessPoints: false,
    gapDensity: false
  });
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [gapFilterSpecialty, setGapFilterSpecialty] = useState('');
  const [gapFilterResultType, setGapFilterResultType] = useState('');
  const [filteredGapCountByCounty, setFilteredGapCountByCounty] = useState(null);
  const [filteredGapCountLoading, setFilteredGapCountLoading] = useState(false);
  const [gapsForCounty, setGapsForCounty] = useState(null);
  const [gapsLoading, setGapsLoading] = useState(false);
  const [geocodeLoading, setGeocodeLoading] = useState(false);
  const [geocodeMessage, setGeocodeMessage] = useState(null);

  const [debugMetrics, setDebugMetrics] = useState({});

  const fetchMapData = useCallback(
    (sid) => {
      if (!apiBase) return;
      setMapDataLoading(true);
      const t0 = performance.now();
      const q = sid ? `?scan_id=${sid}` : '';
      naLog('fetchMapData started', { scanId: sid });
      fetch(`${apiBase}/map-data${q}`, { credentials: 'include' })
        .then((r) => r.json())
        .then((d) => {
          const elapsed = Math.round(performance.now() - t0);
          const provCount = d?.providers?.length ?? 0;
          const facCount = d?.facilities?.length ?? 0;
          naLog('map-data fetched', { elapsed: `${elapsed}ms`, providers: provCount, facilities: facCount });
          setDebugMetrics((prev) => ({ ...prev, mapDataMs: elapsed, providers: provCount, facilities: facCount }));
          if (d.success) setMapData(d);
          setMapDataLoading(false);
        })
        .catch((e) => {
          naLog('map-data fetch error', e);
          setMapDataLoading(false);
        });
    },
    [apiBase]
  );

  useEffect(() => {
    fetchMapData(scanId);
  }, [scanId, fetchMapData]);

  const handleGeocode = useCallback(async () => {
    const providerFileIds = mapData?.provider_file_ids || [];
    const facilityFileIds = mapData?.facility_file_ids || [];
    if ((!providerFileIds.length && !facilityFileIds.length) || !apiBase) {
      setGeocodeMessage('No provider or facility files in this scan.');
      return;
    }
    setGeocodeLoading(true);
    setGeocodeMessage('Geocoding… may take 1–2 min. Please wait.');
    const parseResponse = async (r) => {
      const text = await r.text();
      try {
        return JSON.parse(text);
      } catch {
        throw new Error(r.ok ? 'Invalid response from server' : text.slice(0, 200) || `HTTP ${r.status}`);
      }
    };
    const limit = 200;
    try {
      const results = { providers: { geocoded: 0, failed: 0, fallbackUsed: 0 }, facilities: { geocoded: 0, failed: 0, fallbackUsed: 0 } };
      const [provRes, facRes] = await Promise.all([
        providerFileIds.length
          ? fetch(`${apiBase}/geocode/providers`, {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ source_upload_file_ids: providerFileIds, limit })
            })
          : null,
        facilityFileIds.length
          ? fetch(`${apiBase}/geocode/facilities`, {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ source_upload_file_ids: facilityFileIds, limit })
            })
          : null
      ]);
      if (provRes) {
        const d = await parseResponse(provRes);
        if (d.success) results.providers = d;
        else if (!provRes.ok) throw new Error(d.message || `Geocode providers: HTTP ${provRes.status}`);
      }
      if (facRes) {
        const d = await parseResponse(facRes);
        if (d.success) results.facilities = d;
        else if (!facRes.ok) throw new Error(d.message || `Geocode facilities: HTTP ${facRes.status}`);
      }
      const pGeocoded = results.providers.geocoded || 0;
      const fGeocoded = results.facilities.geocoded || 0;
      const pFailed = results.providers.failed || 0;
      const fFailed = results.facilities.failed || 0;
      const fallback = (results.providers.fallbackUsed || 0) + (results.facilities.fallbackUsed || 0);
      let msg = `Done. Geocoded ${pGeocoded} providers, ${fGeocoded} facilities.`;
      if (pFailed || fFailed) msg += ` Failed: ${pFailed} providers, ${fFailed} facilities.`;
      if (fallback) msg += ` ${fallback} used ZIP/county centroid (no street match).`;
      setGeocodeMessage(msg);
      if (pGeocoded || fGeocoded) {
        fetchMapData(scanId || (scans?.length > 0 ? scans[0]?.id : null));
      }
    } catch (e) {
      setGeocodeMessage('Geocode failed: ' + (e?.message || 'Unknown error'));
    } finally {
      setGeocodeLoading(false);
    }
  }, [apiBase, mapData?.provider_file_ids, mapData?.facility_file_ids, scanId, scans, fetchMapData]);

  const [countyGeoJsonRequested, setCountyGeoJsonRequested] = useState(false);
  const hasCountyData = !!(
    mapData?.results?.length ||
    (mapData?.county_status_by_fips && Object.keys(mapData.county_status_by_fips).length > 0) ||
    (mapData?.fips_to_ssacd && Object.keys(mapData.fips_to_ssacd).length > 0)
  );
  useEffect(() => {
    if (!layers.counties || countyGeoJsonRequested || countyGeoJson || countyGeoJsonError) return;
    if (!hasCountyData) return;
    setCountyGeoJsonRequested(true);
    const t0 = performance.now();
    naLog('county GeoJSON fetch started');
    fetch(`${apiBase}${COUNTY_GEOJSON_PATH}`, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => {
        const elapsed = Math.round(performance.now() - t0);
        naLog('county GeoJSON fetched', { elapsed: `${elapsed}ms`, features: data?.features?.length ?? 0 });
        setDebugMetrics((prev) => ({ ...prev, countyGeoJsonMs: elapsed }));
        setCountyGeoJson(data);
      })
      .catch((e) => setCountyGeoJsonError(e.message));
  }, [layers.counties, countyGeoJsonRequested, countyGeoJson, countyGeoJsonError, hasCountyData]);

  const countyStatusByCode = useMemo(() => {
    const m = new Map();
    if (mapData?.county_status_by_fips && Object.keys(mapData.county_status_by_fips).length > 0) {
      for (const [fips, status] of Object.entries(mapData.county_status_by_fips)) {
        const code = normalizeCountyCode(fips);
        if (code && (!m.get(code) || status === 'deficient' || (status === 'fragile' && m.get(code) !== 'deficient')))
          m.set(code, status);
      }
      return m;
    }
    if (!mapData?.results?.length) return m;
    for (const r of mapData.results) {
      const code = normalizeCountyCode(r.ssa_state_county_code);
      if (!code) continue;
      const status = r.cms_baseline_status === 'fail' ? 'deficient' : r.internal_fragile_status === 'fragile' ? 'fragile' : 'compliant';
      const existing = m.get(code);
      if (!existing || status === 'deficient' || (status === 'fragile' && existing !== 'deficient'))
        m.set(code, status);
    }
    return m;
  }, [mapData?.results, mapData?.county_status_by_fips]);

  const countyGapCountByCode = useMemo(() => {
    const m = new Map();
    const rows = (gapFilterSpecialty || gapFilterResultType) && filteredGapCountByCounty
      ? filteredGapCountByCounty
      : (mapData?.gap_count_by_county ?? []);
    for (const r of rows) {
      const code = normalizeCountyCode(r.ssa_state_county_code);
      if (code) m.set(code, r.gap_count ?? 0);
    }
    return m;
  }, [mapData?.gap_count_by_county, filteredGapCountByCounty, gapFilterSpecialty, gapFilterResultType]);

  const resultsForCounty = useMemo(() => {
    if (!selectedCounty || !mapData?.results?.length) return [];
    if (selectedCounty === '00000') return [];
    const code = normalizeCountyCode(selectedCounty);
    const ssacd = mapData?.fips_to_ssacd?.[code] || code;
    return mapData.results.filter((r) => normalizeCountyCode(r.ssa_state_county_code) === normalizeCountyCode(ssacd));
  }, [selectedCounty, mapData?.results, mapData?.fips_to_ssacd]);

  const ssacdForSelectedCounty = selectedCounty && mapData?.fips_to_ssacd
    ? (mapData.fips_to_ssacd[normalizeCountyCode(selectedCounty)] || selectedCounty)
    : selectedCounty;

  const effectiveScanId = scanId || (scans?.length > 0 ? scans[0].id : null);
  const fetchFilteredGapCount = useCallback(() => {
    if (!apiBase || !effectiveScanId || !layers.gapDensity) return;
    if (!gapFilterSpecialty && !gapFilterResultType) {
      setFilteredGapCountByCounty(null);
      return;
    }
    setFilteredGapCountLoading(true);
    const params = new URLSearchParams();
    if (gapFilterSpecialty) params.set('specialty_code', gapFilterSpecialty);
    if (gapFilterResultType) params.set('result_type', gapFilterResultType);
    fetch(`${apiBase}/scans/${effectiveScanId}/gap-count-by-county?${params}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) setFilteredGapCountByCounty(d.gap_count_by_county ?? []);
        else setFilteredGapCountByCounty([]);
        setFilteredGapCountLoading(false);
      })
      .catch(() => { setFilteredGapCountByCounty([]); setFilteredGapCountLoading(false); });
  }, [apiBase, effectiveScanId, layers.gapDensity, gapFilterSpecialty, gapFilterResultType]);

  useEffect(() => {
    if (layers.gapDensity && (gapFilterSpecialty || gapFilterResultType)) fetchFilteredGapCount();
    else setFilteredGapCountByCounty(null);
  }, [layers.gapDensity, gapFilterSpecialty, gapFilterResultType, fetchFilteredGapCount]);

  const [spatialGapsDebug, setSpatialGapsDebug] = useState(null);

  const fetchGapsForCounty = useCallback(() => {
    if (!apiBase || !effectiveScanId || !ssacdForSelectedCounty) return;
    setGapsLoading(true);
    setSpatialGapsDebug(null);
    const params = new URLSearchParams({ ssa_state_county_code: ssacdForSelectedCounty });
    if (gapFilterSpecialty) params.set('specialty_code', gapFilterSpecialty);
    if (gapFilterResultType) params.set('result_type', gapFilterResultType);
    params.set('_debug', '1');
    const url = `${apiBase}/scans/${effectiveScanId}/spatial-gaps?${params}`;
    naLog('fetchGapsForCounty', { url, ssacdForSelectedCounty, gapFilterSpecialty, gapFilterResultType });
    fetch(url, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setGapsForCounty(d.gaps || []);
          if (d._spatial_gaps_debug) setSpatialGapsDebug(d._spatial_gaps_debug);
        } else {
          setGapsForCounty([]);
        }
        setGapsLoading(false);
      })
      .catch((e) => {
        naLog('fetchGapsForCounty error', e);
        setGapsForCounty([]);
        setGapsLoading(false);
      });
  }, [apiBase, effectiveScanId, ssacdForSelectedCounty, gapFilterSpecialty, gapFilterResultType]);

  useEffect(() => {
    if (!selectedCounty) {
      setGapsForCounty(null);
      setSpatialGapsDebug(null);
    }
  }, [selectedCounty]);

  useEffect(() => {
    if (selectedCounty && layers.gapDensity && ssacdForSelectedCounty) fetchGapsForCounty();
  }, [selectedCounty, layers.gapDensity, ssacdForSelectedCounty, gapFilterSpecialty, gapFilterResultType, fetchGapsForCounty]);

  const countyStyle = useCallback(
    (feature) => {
      const code = extractCountyCodeFromFeature(feature);
      if (!code) return { fillColor: STATUS_COLORS.none, fillOpacity: 0.55, color: '#475569', weight: 1 };
      const normalized = normalizeCountyCode(code);
      if (layers.gapDensity) {
        const gapCount = countyGapCountByCode.get(normalized) ?? countyGapCountByCode.get(code) ?? 0;
        const fillColor = gapCount === 0 ? STATUS_COLORS.none : gapCount <= 5 ? '#fca5a5' : gapCount <= 15 ? '#dc2626' : '#991b1b';
        return { fillColor, fillOpacity: gapCount > 0 ? 0.65 : 0.4, color: '#475569', weight: 1 };
      }
      const status = countyStatusByCode.get(normalized) || countyStatusByCode.get(code) || 'none';
      return {
        fillColor: STATUS_COLORS[status] || STATUS_COLORS.none,
        fillOpacity: 0.55,
        color: '#475569',
        weight: 1
      };
    },
    [countyStatusByCode, countyGapCountByCode, layers.gapDensity]
  );

  const onSelectProvider = useCallback((p) => {
    setSelectedProvider(p);
    setSelectedFacility(null);
    setSelectedCounty(null);
  }, []);

  const onSelectFacility = useCallback((f) => {
    setSelectedFacility(f);
    setSelectedProvider(null);
    setSelectedCounty(null);
  }, []);

  const onCountyClick = useCallback((e) => {
    const layer = e.target;
    const feature = layer?.feature;
    const code = extractCountyCodeFromFeature(feature);
    if (code) {
      setSelectedCounty(normalizeCountyCode(code));
    } else {
      setSelectedCounty(null);
    }
    setSelectedProvider(null);
    setSelectedFacility(null);
  }, []);

  const onEachCountyFeature = useCallback(
    (feature, layer) => {
      layer.on({ click: onCountyClick });
    },
    [onCountyClick]
  );

  const relevantFips = useMemo(() => {
    const set = new Set();
    const add = (code) => {
      const n = normalizeCountyCode(code || '');
      if (n) set.add(n);
    };
    const statusByFips = mapData?.county_status_by_fips || {};
    const fipsToSsacd = mapData?.fips_to_ssacd || {};
    Object.keys(statusByFips).forEach(add);
    Object.keys(fipsToSsacd).forEach(add);
    (mapData?.results || []).forEach((r) => add(r.ssa_state_county_code));
    return set;
  }, [mapData]);

  const providerItems = useMemo(() => {
    const filtered = (mapData?.providers || []).filter((p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude));
    return spreadDuplicateCoordinates(filtered);
  }, [mapData?.providers]);
  const facilityItems = useMemo(() => {
    const filtered = (mapData?.facilities || []).filter((f) => Number.isFinite(f.latitude) && Number.isFinite(f.longitude));
    return spreadDuplicateCoordinates(filtered);
  }, [mapData?.facilities]);

  const countyGeoJsonWithHandlers = useMemo(() => {
    if (!countyGeoJson || relevantFips.size === 0) return null;
    const t0 = performance.now();
    const filtered = (countyGeoJson.features || []).filter((f) => {
      const code = extractCountyCodeFromFeature(f);
      return code && (relevantFips.has(code) || relevantFips.has(normalizeCountyCode(code)));
    });
    if (filtered.length === 0) return null;
    const features = filtered.map((f) => {
      const geom = f.geometry;
      if (!geom || !Array.isArray(geom.coordinates)) return f;
      if (geom.type === 'Polygon') {
        const simplified = geom.coordinates.map((ring) => simplifyRing(ring, 2));
        return { ...f, geometry: { ...geom, coordinates: simplified } };
      }
      if (geom.type === 'MultiPolygon') {
        const simplified = geom.coordinates.map((poly) => poly.map((ring) => simplifyRing(ring, 2)));
        return { ...f, geometry: { ...geom, coordinates: simplified } };
      }
      return f;
    });
    naLog('countyGeoJsonWithHandlers computed', { filtered: filtered.length, elapsed: `${Math.round(performance.now() - t0)}ms` });
    return { type: countyGeoJson.type, features };
  }, [countyGeoJson, relevantFips]);

  useEffect(() => {
    if (countyGeoJsonWithHandlers && NA_MAP_DEBUG) {
      setDebugMetrics((prev) => ({ ...prev, countyFeatures: countyGeoJsonWithHandlers.features?.length }));
    }
  }, [countyGeoJsonWithHandlers]);

  const latestScanId = scans.length > 0 ? scans[0].id : null;

  const onGapFilterChange = useCallback((specialty, resultType) => {
    setGapFilterSpecialty(specialty || '');
    setGapFilterResultType(resultType || '');
    setGapsForCounty(null);
  }, []);

  const onClearSelection = useCallback(() => {
    setSelectedCounty(null);
    setSelectedProvider(null);
    setSelectedFacility(null);
    setGapsForCounty(null);
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <MapPanel
        apiBase={apiBase}
        scans={scans}
        loadingScans={loadingScans}
        scanId={effectiveScanId}
        onScanChange={setScanId}
        onRefreshScans={onRefreshScans}
        mapDataLoading={mapDataLoading}
        layers={layers}
        onLayersChange={setLayers}
        selectedCounty={selectedCounty}
        selectedProvider={selectedProvider}
        selectedFacility={selectedFacility}
        resultsForCounty={resultsForCounty}
        mapData={mapData}
        gapsForCounty={gapsForCounty}
        gapsLoading={gapsLoading}
        spatialGapsDebug={spatialGapsDebug}
        onFetchGapsForCounty={fetchGapsForCounty}
        gapFilterSpecialty={gapFilterSpecialty}
        gapFilterResultType={gapFilterResultType}
        onGapFilterChange={onGapFilterChange}
        filteredGapCountLoading={filteredGapCountLoading}
        onClearSelection={onClearSelection}
        onGeocode={handleGeocode}
        geocodeLoading={geocodeLoading}
        geocodeMessage={geocodeMessage}
      />
      {NA_MAP_DEBUG && Object.keys(debugMetrics).length > 0 && (
        <div className="rounded-xl border border-blue-300 bg-blue-50 p-4 text-sm font-mono">
          <div className="font-semibold text-blue-900 mb-2">[NAMapView] Debug — set REACT_APP_NA_MAP_DEBUG=0 to hide</div>
          <div className="text-blue-800 space-y-1 text-xs">
            {debugMetrics.mapDataMs != null && <div>map-data fetch: {debugMetrics.mapDataMs}ms</div>}
            {debugMetrics.providers != null && <div>providers: {debugMetrics.providers}</div>}
            {debugMetrics.facilities != null && <div>facilities: {debugMetrics.facilities}</div>}
            {debugMetrics.countyGeoJsonMs != null && <div>county GeoJSON load: {debugMetrics.countyGeoJsonMs}ms</div>}
            {debugMetrics.countyFeatures != null && <div>county features: {debugMetrics.countyFeatures}</div>}
            <div className="text-blue-600 mt-2">Check browser console for [NAMapView] logs (FitBounds, ImperativeMarkersLayer runs)</div>
          </div>
        </div>
      )}
      {mapData?._map_diagnostics?.root_cause && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm">
          <div className="font-semibold text-amber-900 mb-2">Map diagnostics — why providers/facilities may not show</div>
          <div className="text-amber-800 space-y-1 font-mono text-xs">
            <div><strong>Root cause:</strong> {mapData._map_diagnostics.root_cause}</div>
            {mapData._map_diagnostics.provider_file_ids_count != null && <div>Provider file IDs in scan: {mapData._map_diagnostics.provider_file_ids_count} · County codes from results: {mapData._map_diagnostics.county_codes_count}</div>}
            {mapData._map_diagnostics.providers_in_db_by_files != null && <div>Providers in DB (by files): {mapData._map_diagnostics.providers_in_db_by_files} · With stored coords: {mapData._map_diagnostics.providers_with_stored_coords}</div>}
            {mapData._map_diagnostics.ref_counties_with_coords != null && <div>Reference counties with coords: {mapData._map_diagnostics.ref_counties_with_coords} · ZIP centroids: {mapData._map_diagnostics.zip_centroids_count}</div>}
            {mapData._map_diagnostics.fallback_used && <div className="text-green-700 font-medium">Fallback applied: {mapData._map_diagnostics.fallback_used}</div>}
            {mapData._map_diagnostics.facility_fallback_used && <div className="text-green-700 font-medium">Facility fallback: {mapData._map_diagnostics.facility_fallback_used}</div>}
          </div>
        </div>
      )}
      <div className="rounded-xl overflow-hidden border border-slate-200 bg-white shadow-md" style={{ minHeight: 560 }}>
        {countyGeoJsonError && (
          <div className="p-2 bg-amber-100 text-amber-800 text-sm">
            County boundaries: {countyGeoJsonError}. Using providers/facilities only.
          </div>
        )}
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          maxZoom={19}
          style={{ height: '100%', minHeight: 560 }}
          className="z-0"
        >
          <TileLayer
            attribution={MAP_TILE_ATTRIBUTION}
            url={MAP_TILE_URL}
          />
          <FitBoundsToMarkers providers={providerItems} facilities={facilityItems} />
          {layers.counties && countyGeoJsonWithHandlers && (
            <GeoJSON
              key="counties"
              data={countyGeoJsonWithHandlers}
              style={countyStyle}
              onEachFeature={onEachCountyFeature}
            />
          )}
          {layers.providers && providerItems.length > 0 && (
            <ImperativeClusterLayer
              items={providerItems}
              icon={PROVIDER_ICON}
              label="Provider"
              onSelect={onSelectProvider}
              debugId="providers"
            />
          )}
          {layers.facilities && facilityItems.length > 0 && (
            <ImperativeClusterLayer
              items={facilityItems}
              icon={FACILITY_ICON}
              label="Facility"
              onSelect={onSelectFacility}
              debugId="facilities"
            />
          )}
          {layers.accessPoints && mapData?.access_points?.length > 0 && (
            <LayerGroup key="access-points">
              {mapData.access_points.map((a, i) => (
                <CircleMarker
                  key={a.id || `ap-${i}`}
                  center={[a.latitude, a.longitude]}
                  radius={3}
                  pathOptions={{ fillColor: '#94a3b8', color: '#64748b', weight: 1, fillOpacity: 0.7 }}
                />
              ))}
            </LayerGroup>
          )}
          {layers.gapDensity && gapsForCounty?.length > 0 && (
            <LayerGroup key="gap-origins">
              {gapsForCounty.map((g, i) => (
                <CircleMarker
                  key={g.id || `gap-${i}`}
                  center={[g.origin_lat, g.origin_lon]}
                  radius={6}
                  pathOptions={{ fillColor: '#dc2626', color: '#991b1b', weight: 2, fillOpacity: 0.85 }}
                >
                  <Popup>
                    <strong>Spatial gap</strong><br />
                    {g.specialty_code} ({g.result_type})<br />
                    {g.time_gap_minutes != null && `Time gap: ${g.time_gap_minutes.toFixed(0)} min`}
                    {g.time_gap_minutes != null && g.distance_gap_miles != null && ' · '}
                    {g.distance_gap_miles != null && `Distance gap: ${g.distance_gap_miles.toFixed(1)} mi`}
                    {g.members_at_risk != null && <><br />{g.members_at_risk.toLocaleString()} members at risk</>}
                  </Popup>
                </CircleMarker>
              ))}
            </LayerGroup>
          )}
        </MapContainer>
        <div className="flex flex-wrap items-center gap-4 px-3 py-2 bg-white border-t border-slate-200 text-sm">
          <span className="font-medium text-slate-600">Legend:</span>
          <span><span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-1" />Compliant</span>
          <span><span className="inline-block w-3 h-3 rounded-full bg-amber-500 mr-1" />Fragile</span>
          <span><span className="inline-block w-3 h-3 rounded-full bg-red-600 mr-1" />Deficient</span>
          {layers.gapDensity && gapsForCounty?.length > 0 && (
            <span><span className="inline-block w-3 h-3 rounded-full bg-red-600 border-2 border-red-800 mr-1" />Spatial gap origin</span>
          )}
        </div>
      </div>
    </div>
  );
}

function getResultStatus(r) {
  if (r.cms_baseline_status === 'fail') return 'deficient';
  if (r.internal_fragile_status === 'fragile') return 'fragile';
  return 'compliant';
}

function getIssueType(r) {
  const countFail = r.access_standard_met === false;
  const tdFail = r.time_distance_evaluated && r.time_distance_met === false;
  if (countFail && tdFail) return 'both';
  if (countFail) return 'count';
  if (tdFail) return 'time_distance';
  return null;
}

function buildWhyFailingInsight(resultsForCounty) {
  if (!resultsForCounty?.length) return null;
  const deficient = resultsForCounty.filter((r) => r.cms_baseline_status === 'fail');
  const fragile = resultsForCounty.filter((r) => r.cms_baseline_status !== 'fail' && r.internal_fragile_status === 'fragile');
  const parts = [];
  if (deficient.length > 0) {
    const countOnly = deficient.filter((r) => getIssueType(r) === 'count');
    const tdOnly = deficient.filter((r) => getIssueType(r) === 'time_distance');
    const both = deficient.filter((r) => getIssueType(r) === 'both');
    if (countOnly.length > 0) {
      const specs = countOnly.map((r) => r.specialty_label || r.specialty_code).join(', ');
      parts.push(`This county is deficient primarily due to ${countOnly.length === 1 ? 'specialty' : 'specialties'} ${specs} falling below required count.`);
    }
    if (tdOnly.length > 0) {
      const withPct = tdOnly.find((r) => r.pct_population_within_time_distance_standard != null);
      const pct = withPct ? Math.round(Number(withPct.pct_population_within_time_distance_standard)) : null;
      const specs = tdOnly.map((r) => r.specialty_label || r.specialty_code).join(', ');
      parts.push(`This county fails drive time/distance for ${specs}${pct != null ? `; only ${pct}% of member locations are within standard` : '.'}`);
    }
    if (both.length > 0) {
      const specs = both.map((r) => r.specialty_label || r.specialty_code).join(', ');
      parts.push(`This county has both count and time/distance deficiencies for ${specs}.`);
    }
  }
  if (fragile.length > 0 && parts.length === 0) {
    parts.push('This county is fragile because count meets minimum, but margin is narrow.');
  }
  return parts.length > 0 ? parts.join(' ') : null;
}

const MapPanel = memo(function MapPanel({
  apiBase,
  scans,
  loadingScans,
  scanId,
  onScanChange,
  onRefreshScans,
  mapDataLoading,
  layers,
  onLayersChange,
  selectedCounty,
  selectedProvider,
  selectedFacility,
  resultsForCounty,
  mapData,
  gapsForCounty = null,
  gapsLoading = false,
  spatialGapsDebug = null,
  onFetchGapsForCounty = () => {},
  gapFilterSpecialty = '',
  gapFilterResultType = '',
  onGapFilterChange = () => {},
  filteredGapCountLoading = false,
  onClearSelection,
  onGeocode = null,
  geocodeLoading = false,
  geocodeMessage = null
}) {
  const countyName = useMemo(() => {
    if (!selectedCounty || !mapData?.counties?.length) return selectedCounty || '';
    const code = normalizeCountyCode(selectedCounty);
    const c = mapData.counties.find(
      (x) => normalizeCountyCode(x.ssa_state_county_code) === code
    );
    return c ? [c.county_name, c.state_name].filter(Boolean).join(', ') : selectedCounty;
  }, [selectedCounty, mapData?.counties]);

  const countyOverallStatus = useMemo(() => {
    if (!resultsForCounty?.length) return 'none';
    let status = 'compliant';
    for (const r of resultsForCounty) {
      const s = getResultStatus(r);
      if (s === 'deficient') return 'deficient';
      if (s === 'fragile') status = 'fragile';
    }
    return status;
  }, [resultsForCounty]);

  const failingSpecialties = useMemo(
    () => (resultsForCounty || []).filter((r) => getResultStatus(r) !== 'compliant'),
    [resultsForCounty]
  );

  const whyFailingInsight = useMemo(() => buildWhyFailingInsight(resultsForCounty), [resultsForCounty]);

  const uniqueSpecialtiesFromScan = useMemo(() => {
    const seen = new Set();
    return (mapData?.results ?? [])
      .map((r) => ({ code: r.specialty_code, label: r.specialty_label || r.specialty_code }))
      .filter((x) => {
        if (!x.code) return false;
        if (seen.has(x.code)) return false;
        seen.add(x.code);
        return true;
      });
  }, [mapData?.results]);

  const uniqueResultTypesFromScan = useMemo(() => {
    return [...new Set((mapData?.results ?? []).map((r) => r.result_type).filter(Boolean))];
  }, [mapData?.results]);

  const statusBadgeClass = {
    compliant: 'bg-green-100 text-green-800',
    fragile: 'bg-amber-100 text-amber-800',
    deficient: 'bg-red-100 text-red-800',
    none: 'bg-slate-100 text-slate-600'
  };

  return (
    <div className="rounded-xl border border-slate-300 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-slate-600">Scan</label>
          <select
            className="rounded border border-slate-300 px-2 py-1.5 text-sm"
            value={scanId || ''}
            onChange={(e) => onScanChange(e.target.value || null)}
            disabled={loadingScans}
          >
            <option value="">Latest</option>
            {(scans || []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.scan_timestamp ? new Date(s.scan_timestamp).toLocaleString() : s.id}
              </option>
            ))}
          </select>
          {onRefreshScans && (
            <button
              type="button"
              className="text-slate-500 hover:text-slate-700"
              onClick={onRefreshScans}
              aria-label="Refresh scans"
            >
              ↻
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          {['counties', 'providers', 'facilities', 'accessPoints', 'gapDensity'].map((key) => (
            <label key={key} className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={layers[key] ?? false}
                onChange={(e) => onLayersChange((prev) => ({ ...prev, [key]: e.target.checked }))}
              />
              <span title={key === 'accessPoints' ? 'Sample locations where members might live (ZIP centers, Census points). We measure drive time from each to the nearest provider.' : key === 'gapDensity' ? 'Count of deficiencies per county. Redder = more gaps.' : undefined}>
                {key === 'accessPoints' ? 'Member locations' : key === 'gapDensity' ? 'Gap density' : key.charAt(0).toUpperCase() + key.slice(1)}
              </span>
            </label>
          ))}
          {layers.gapDensity && (
            <>
              <span className="text-xs text-slate-500 flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: '#fca5a5' }} title="1–5 gaps" />
                <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: '#dc2626' }} title="6–15 gaps" />
                <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: '#991b1b' }} title="16+ gaps" />
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Gap filter:</span>
                <select
                  className="rounded border border-slate-300 px-2 py-1 text-xs"
                  value={gapFilterSpecialty}
                  onChange={(e) => onGapFilterChange(e.target.value, gapFilterResultType)}
                >
                  <option value="">All specialties</option>
                  {uniqueSpecialtiesFromScan.map((s) => (
                    <option key={s.code} value={s.code}>{s.label}</option>
                  ))}
                </select>
                <select
                  className="rounded border border-slate-300 px-2 py-1 text-xs"
                  value={gapFilterResultType}
                  onChange={(e) => onGapFilterChange(gapFilterSpecialty, e.target.value)}
                >
                  <option value="">All types</option>
                  {uniqueResultTypesFromScan.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                {filteredGapCountLoading && <span className="text-xs text-slate-500">Loading…</span>}
              </div>
            </>
          )}
        </div>
        {(selectedCounty || selectedProvider || selectedFacility) && (
          <button
            type="button"
            className="text-slate-500 hover:text-slate-700 text-sm"
            onClick={onClearSelection}
          >
            Clear selection
          </button>
        )}
      </div>
      {mapDataLoading && <p className="text-sm text-slate-500 mt-2">Loading map data…</p>}
      {onGeocode && !mapDataLoading && (
        <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
          <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">True addresses</h4>
          <p className="text-xs text-slate-500 mb-2">Providers/facilities without geocoded addresses show at ZIP centroids (same point for many). Run Geocode to resolve street addresses for accurate map locations.</p>
          <button
            type="button"
            onClick={onGeocode}
            disabled={geocodeLoading}
            className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg"
          >
            {geocodeLoading ? 'Geocoding…' : 'Geocode providers & facilities'}
          </button>
          {geocodeMessage && <p className="text-xs mt-2 text-slate-600">{geocodeMessage}</p>}
        </div>
      )}
      {selectedCounty && selectedCounty !== '00000' && (
        <div className="mt-4 space-y-4">
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-medium text-slate-800">{countyName || selectedCounty}</h3>
              <span className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${statusBadgeClass[countyOverallStatus] || statusBadgeClass.none}`}>
                {countyOverallStatus}
              </span>
            </div>
            {failingSpecialties.length > 0 && (
              <p className="text-xs text-slate-600 mt-1">Failing specialties: {failingSpecialties.map((r) => r.specialty_label || r.specialty_code).join(', ')}</p>
            )}
          </div>
          {(() => {
            const code = normalizeCountyCode(selectedCounty);
            const fipsToSsacd = mapData?.fips_to_ssacd || {};
            const ssacd = fipsToSsacd[code] || selectedCounty;
            const lookupKey = normalizeCountyCode(ssacd);
            const byCounty = mapData?.member_impact_by_county || {};
            const impact = byCounty[lookupKey] ?? byCounty[code] ?? byCounty[ssacd];
            if (!impact) return null;
            const total = impact.total_members ?? 0;
            const atRisk = impact.members_at_risk ?? 0;
            const source = impact.membership_source || (total > 0 ? 'enrollment' : null);
            if (total === 0 && atRisk === 0 && !source) return null;
            return (
              <div className="p-3 bg-violet-50 rounded-lg border border-violet-200">
                <h4 className="text-xs font-semibold text-violet-800 uppercase tracking-wide mb-2">Member impact</h4>
                <ul className="text-sm text-slate-700 space-y-1">
                  {total > 0 && <li><strong>{total.toLocaleString()}</strong> total members in county {source ? <span className="text-slate-500">({source})</span> : ''}</li>}
                  {atRisk > 0 && <li><strong className="text-violet-600">{atRisk.toLocaleString()}</strong> members at risk for failing specialties</li>}
                </ul>
              </div>
            );
          })()}
          {whyFailingInsight && (
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Why failing</h4>
              <p className="text-sm text-slate-700">{whyFailingInsight}</p>
            </div>
          )}
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Specialty details</h4>
            {resultsForCounty.length === 0 ? (
              <p className="text-sm text-slate-500">No scan results for this county.</p>
            ) : (
              <ul className="space-y-2 text-sm text-slate-700">
                {resultsForCounty.map((r) => {
                  const status = getResultStatus(r);
                  const issueType = getIssueType(r);
                  const tdPct = r.pct_population_within_time_distance_standard != null ? Number(r.pct_population_within_time_distance_standard).toFixed(0) : null;
                  return (
                    <li key={`${r.specialty_code}-${r.result_type}`} className="flex flex-wrap items-baseline gap-x-2">
                      <span className="font-medium">{r.specialty_label || r.specialty_code}</span>
                      <span className="text-slate-500">({r.result_type})</span>
                      <span>required {r.required_count}, actual {r.actual_count}</span>
                      {r.time_distance_evaluated && (
                        <span title="Drive time & distance: % of member locations within CMS standard (90% metro, 85% rural).">
                          · Drive time: {tdPct != null ? `${tdPct}%` : '—'} within standard
                        </span>
                      )}
                      {status === 'deficient' && <span className="text-red-600 font-medium">(deficient)</span>}
                      {status === 'fragile' && <span className="text-amber-600 font-medium">(fragile)</span>}
                      {issueType && <span className="text-xs text-slate-500">[{issueType}]</span>}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Remediation summary</h4>
            <p className="text-xs text-slate-500 mb-2 flex items-start gap-1">
              Count gaps (required vs actual) differ from spatial gaps (member locations that failed drive time/distance). Spatial gaps appear when drive-time evaluation ran.
              <InfoTooltip text="Count gap = not enough providers. Spatial gap = member locations too far from nearest provider (failed drive-time standard)." />
            </p>
            {failingSpecialties.length === 0 ? (
              <p className="text-sm text-slate-600">No deficiencies in this county.</p>
            ) : (
              <ul className="space-y-1.5 text-sm text-slate-700">
                {failingSpecialties.map((r) => {
                  const gap = Math.max(0, (r.required_count ?? 0) - (r.actual_count ?? 0));
                  const tdPct = r.pct_population_within_time_distance_standard != null ? Number(r.pct_population_within_time_distance_standard).toFixed(0) : null;
                  const issueType = getIssueType(r);
                  return (
                    <li key={`${r.specialty_code}-${r.result_type}`}>
                      <strong>{r.specialty_label || r.specialty_code}</strong> ({r.result_type}): required {r.required_count}, actual {r.actual_count}
                      {gap > 0 && <span className="text-red-600"> · Gap: {gap}</span>}
                      {r.members_at_risk != null && <span className="text-violet-600"> · {r.members_at_risk.toLocaleString()} members at risk</span>}
                      {r.time_distance_evaluated && tdPct != null && (
                        <span title="Drive time & distance: % of member locations within CMS standard."> · Drive time: {tdPct}% within standard</span>
                      )}
                      {issueType === 'time_distance' && (
                        <span className="text-slate-600" title="Members at some locations are too far from the nearest provider."> · Deficiency is drive-time based.</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {layers.gapDensity && (
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Spatial gaps</h4>
              <p className="text-xs text-slate-500 mb-2 flex items-start gap-1">
                Member locations that failed drive time/distance. Plotted on map when selected.
                <InfoTooltip text="Locations where the nearest provider is too far (beyond CMS limit). Each gap = one location that failed." />
              </p>
              {gapsForCounty == null ? (
                <button
                  type="button"
                  className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                  onClick={onFetchGapsForCounty}
                  disabled={gapsLoading}
                >
                  {gapsLoading ? 'Loading…' : 'View gaps'}
                </button>
              ) : (
                <>
                  <p className="text-sm text-slate-700 mb-2">{gapsForCounty.length} gap(s) in this county</p>
                  {gapsForCounty.length === 0 && spatialGapsDebug && (
                    <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs font-mono text-amber-900 mb-2">
                      <div className="font-semibold mb-1">Spatial gaps debug (why 0?)</div>
                      <div>Total gaps in scan: {spatialGapsDebug.total_gaps_in_scan}</div>
                      <div>Gaps for this county (no specialty/type filter): {spatialGapsDebug.gaps_for_this_county_no_filter ?? '—'}</div>
                      <div>Params: county={spatialGapsDebug.params?.ssa_state_county_code}, specialty={spatialGapsDebug.params?.specialty_code}, result_type={spatialGapsDebug.params?.result_type}</div>
                      <div>County norm sent: {spatialGapsDebug.county_norm_sent ?? '—'}</div>
                      <div>SSACDs in DB (sample): {spatialGapsDebug.distinct_ssacds?.join(', ') || '—'}</div>
                      {spatialGapsDebug.gaps_for_this_county_no_filter > 0 && <div className="text-amber-700 mt-1">Gaps exist for county but filter excluded them. Try &quot;All specialties&quot; / &quot;All types&quot;.</div>}
                      {spatialGapsDebug.total_gaps_in_scan === 0 && <div className="text-amber-700 mt-1">No spatial gaps in scan. Run scan with NA_TIME_DISTANCE_ENABLED=true.</div>}
                    </div>
                  )}
                  {gapsForCounty.length > 0 && (
                    <p className="text-sm text-violet-700 font-medium mb-2">
                      Total members at risk in this county: {gapsForCounty.reduce((sum, g) => sum + (g.members_at_risk ?? 0), 0).toLocaleString()}
                    </p>
                  )}
                  {gapsForCounty.length > 0 && (
                    <ul className="space-y-1.5 text-xs text-slate-600 max-h-48 overflow-y-auto">
                      {gapsForCounty.slice(0, 50).map((g) => (
                        <li key={g.id}>
                          {g.specialty_code} ({g.result_type}): origin ({g.origin_lat?.toFixed(2)}, {g.origin_lon?.toFixed(2)})
                          {g.time_gap_minutes != null && ` · time gap ${g.time_gap_minutes.toFixed(0)} min`}
                          {g.distance_gap_miles != null && ` · dist gap ${g.distance_gap_miles.toFixed(1)} mi`}
                          {g.members_at_risk != null && <span className="text-violet-600"> · {g.members_at_risk.toLocaleString()} members at risk</span>}
                        </li>
                      ))}
                      {gapsForCounty.length > 50 && <li className="text-slate-500">… and {gapsForCounty.length - 50} more</li>}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
      {selectedProvider && (
        <div className="mt-4 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
          <h3 className="font-medium text-slate-800">Provider</h3>
          <p className="text-sm text-slate-700">{selectedProvider.name || selectedProvider.id}</p>
          <p className="text-xs text-slate-500">Specialty {selectedProvider.specialty_code} · County {selectedProvider.ssa_state_county_code}</p>
        </div>
      )}
      {selectedFacility && (
        <div className="mt-4 p-3 bg-sky-50 rounded-lg border border-sky-200">
          <h3 className="font-medium text-slate-800">Facility</h3>
          <p className="text-sm text-slate-700">{selectedFacility.name || selectedFacility.id}</p>
          <p className="text-xs text-slate-500">Type {selectedFacility.specialty_code} · County {selectedFacility.ssa_state_county_code}</p>
        </div>
      )}
    </div>
  );
});
